import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { showBanner } from 'workflow/common/common.actions'

import { sdkTagsKeys } from 'common/api/queryKeys/sdkTagsKeys'
import Button from 'common/components/button'
import ButtonWithTooltip from 'common/components/ButtonWithTooltip'
import ConfirmationModal from 'common/components/confirmationModal'
import { WidgetError } from 'common/components/WidgetMessage'
import { formatDate } from 'common/helpers/date'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { Copy } from 'common/icons'
import { ReactComponent as AddIcon } from 'common/icons/add/default.svg'

import { revokeSdkTag, SdkTag } from './api'
import CreateTagModal from './CreateTagModal'
import EditTagModal from './EditTagModal'

type Props = {
  tags: SdkTag[] | undefined
  isError: boolean
  onTagCreated: (tagId: string) => void
}

const StatusBadge = ({ status }: { status: SdkTag['status'] }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-coolGray-100 text-coolGray-500">
      Revoked
    </span>
  )
}

const TagList = ({ tags, isError, onTagCreated }: Props) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [tagToRevoke, setTagToRevoke] = useState<SdkTag | null>(null)
  const [isRevoking, setIsRevoking] = useState(false)
  const [tagToEdit, setTagToEdit] = useState<SdkTag | null>(null)

  const handleRevoke = async () => {
    if (!tagToRevoke) {
      return
    }
    setIsRevoking(true)
    try {
      const updated = await revokeSdkTag({ accountId, id: tagToRevoke._id })
      queryClient.setQueryData<SdkTag[]>(sdkTagsKeys.list(accountId), updated)
      dispatch(showBanner({ type: 'success', content: 'Tag revoked' }))
    } catch {
      dispatch(
        showBanner({
          type: 'error',
          content: 'Failed to revoke tag. Please try again.',
        })
      )
    } finally {
      setIsRevoking(false)
      setTagToRevoke(null)
    }
  }

  const handleCopyTagId = (tagId: string) => {
    navigator.clipboard.writeText(tagId).catch(() => {
      const textarea = document.createElement('textarea')
      textarea.value = tagId
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    })
    dispatch(showBanner({ type: 'success', content: 'Tag id copied' }))
  }

  const isEmpty = !isError && (tags?.length ?? 0) === 0

  const sortedTags = tags?.slice().sort((a, b) => {
    if (a.status === b.status) {
      return 0
    }
    return a.status === 'active' ? -1 : 1
  })

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-medium text-gray-900">SDK tags</h2>
        <Button
          data-cy="sdk-new-tag-button"
          data-testid="sdk-new-tag-button"
          variant="primary"
          onClick={() => setIsCreateOpen(true)}
          prefixIcon={<AddIcon width={24} height={24} />}
        >
          New tag
        </Button>
      </div>

      <p className="mb-6 text-sm text-coolGray-600">
        Tags identify your site to Jacquard. The tag id is public and can be
        revoked at any time.
      </p>

      {isError && <WidgetError />}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-10 text-center text-coolGray-500">
          <p className="mb-4 text-sm">
            No tags yet — create one to get your installation snippet.
          </p>
          <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
            New tag
          </Button>
        </div>
      )}

      {!isEmpty && !isError && (
        <div className="grid gap-4">
          {/* Header row */}
          <div className="grid grid-cols-[2fr_2fr_3fr_2fr_1fr_2fr] gap-4 text-xs font-semibold uppercase tracking-wide text-coolGray-400">
            <span>Name</span>
            <span>Tag id</span>
            <span>Domains</span>
            <span>Created</span>
            <span>Status</span>
            <span />
          </div>

          {sortedTags?.map((tag) => (
            <div
              key={tag._id}
              data-cy="sdk-tag-row"
              data-testid="sdk-tag-row"
              className="grid grid-cols-[2fr_2fr_3fr_2fr_1fr_2fr] items-center gap-4 border-t border-coolGray-100 pt-4"
            >
              <span className="text-sm font-medium text-coolGray-800 truncate">
                {tag.name}
              </span>

              <span className="flex items-center gap-2">
                <code className="font-sourceCodePro text-sm text-coolGray-700">
                  {tag.tag_id}
                </code>
                {tag.status === 'active' && (
                  <ButtonWithTooltip
                    data-cy="sdk-copy-tag-id"
                    data-testid="sdk-copy-tag-id"
                    tooltip={false}
                    tooltipText="Copy tag id"
                    variant="icon"
                    className="py-0 shrink-0"
                    onClick={() => handleCopyTagId(tag.tag_id)}
                  >
                    <Copy />
                  </ButtonWithTooltip>
                )}
              </span>

              <span
                className="flex flex-wrap gap-1"
                data-testid="sdk-tag-domains"
              >
                {tag.domains && tag.domains.length > 0 ? (
                  tag.domains.map((domain) => (
                    <span
                      key={domain}
                      className="inline-flex items-center rounded-full bg-coolGray-100 px-2 py-0.5 font-sourceCodePro text-xs text-coolGray-700"
                    >
                      {domain}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-coolGray-400">All domains</span>
                )}
              </span>

              <span className="text-sm text-coolGray-600">
                {formatDate(tag.created, 'd MMM yyyy')}
              </span>

              <StatusBadge status={tag.status} />

              <span className="flex items-center gap-3">
                {tag.status === 'active' && (
                  <>
                    <Button
                      data-testid="sdk-edit-tag-button"
                      variant="link"
                      onClick={() => setTagToEdit(tag)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => setTagToRevoke(tag)}
                    >
                      Revoke
                    </Button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <CreateTagModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(tagId) => {
          setIsCreateOpen(false)
          onTagCreated(tagId)
        }}
      />

      {tagToEdit && (
        <EditTagModal tag={tagToEdit} open onClose={() => setTagToEdit(null)} />
      )}

      <ConfirmationModal
        data-cy="sdk-revoke-confirm-modal"
        data-testid="sdk-revoke-confirm-modal"
        title="Revoke this tag?"
        confirmationText="Sites using it will stop recording conversions within a minute. This can't be undone."
        open={!!tagToRevoke}
        isLoading={isRevoking}
        onConfirm={handleRevoke}
        onCancel={() => setTagToRevoke(null)}
      />
    </>
  )
}

export default TagList
